import { cx } from "@jugl-web/utils";
import { FC } from "react";

const SKELETON_ROW_COUNT = 6;

type SkeletonColumnConfig = {
  id: string;
  width: number;
  headerBarWidth: number;
  cellContent: "ticket-name" | "team-notes" | "icon-text" | "assignees";
};

const SKELETON_COLUMNS: SkeletonColumnConfig[] = [
  {
    id: "ticket-name",
    width: 368,
    headerBarWidth: 130,
    cellContent: "ticket-name",
  },
  {
    id: "team-notes",
    width: 110,
    headerBarWidth: 60,
    cellContent: "team-notes",
  },
  {
    id: "customer",
    width: 180,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
  {
    id: "status",
    width: 180,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
  {
    id: "assignees",
    width: 180,
    headerBarWidth: 60,
    cellContent: "assignees",
  },
  {
    id: "priority",
    width: 200,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
  {
    id: "due-date",
    width: 200,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
  {
    id: "created-at",
    width: 200,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
];

interface SkeletonBarProps {
  width: number;
  height: number;
  variant?: "primary" | "secondary";
}

const SkeletonBar: FC<SkeletonBarProps> = ({
  width,
  height,
  variant = "secondary",
}) => (
  <div
    className={cx(
      "shrink-0 rounded-lg",
      variant === "primary" ? "bg-grey-200" : "bg-grey-100"
    )}
    style={{ width, height }}
  />
);

const SkeletonCircle: FC = () => (
  <div className="size-4 shrink-0 rounded-full border border-solid border-white bg-grey-200" />
);

const TicketNameCellSkeleton: FC = () => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center gap-1.5">
      <SkeletonCircle />
      <SkeletonBar width={135} height={16} variant="primary" />
    </div>
    <SkeletonBar width={135} height={8} />
  </div>
);

const IconTextCellSkeleton: FC = () => (
  <div className="flex items-center gap-1.5">
    <SkeletonCircle />
    <SkeletonBar width={80} height={16} />
  </div>
);

const AssigneesCellSkeleton: FC = () => (
  <div className="flex items-center">
    {Array.from({ length: 5 }, (_, index) => (
      <div
        key={index}
        className={cx(
          "size-4 shrink-0 rounded-full border border-solid border-white bg-grey-200",
          index < 4 && "-mr-1"
        )}
      />
    ))}
  </div>
);

const SkeletonCellContent: FC<{
  type: SkeletonColumnConfig["cellContent"];
}> = ({ type }) => {
  switch (type) {
    case "ticket-name":
      return <TicketNameCellSkeleton />;
    case "team-notes":
      return <SkeletonCircle />;
    case "icon-text":
      return <IconTextCellSkeleton />;
    case "assignees":
      return <AssigneesCellSkeleton />;
    default:
      return null;
  }
};

interface SkeletonCellProps {
  column: SkeletonColumnConfig;
  variant: "header" | "body";
}

const SkeletonCell: FC<SkeletonCellProps> = ({ column, variant }) => {
  const isTeamNotes = column.cellContent === "team-notes";

  return (
    <div
      className={cx(
        "shrink-0 border-r border-solid border-dark-100 bg-white px-4 last:border-r-0",
        variant === "header" && "flex h-10 items-center",
        variant === "body" && "flex h-[79px] items-center",
        isTeamNotes && "justify-center"
      )}
      style={{ width: column.width }}
    >
      {variant === "header" ? (
        <SkeletonBar width={column.headerBarWidth} height={10} />
      ) : (
        <SkeletonCellContent type={column.cellContent} />
      )}
    </div>
  );
};

export const TicketsTableSkeleton: FC = () => {
  return (
    <div
      aria-busy
      aria-label="Loading tickets"
      className="w-fit min-w-full animate-pulse border-b border-solid border-[#EEF2F5]"
    >
      <div className="flex">
        {SKELETON_COLUMNS.map((column) => (
          <SkeletonCell key={column.id} column={column} variant="header" />
        ))}
      </div>

      {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex border-t border-solid border-[#EEF2F5]"
        >
          {SKELETON_COLUMNS.map((column) => (
            <SkeletonCell key={column.id} column={column} variant="body" />
          ))}
        </div>
      ))}
    </div>
  );
};
